import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { walletService, WalletService } from '../services/walletService';
import { WalletSessionResponse, WalletSessionStatusResponse } from '../services/walletSessionService';

export type WalletStatus = 'disconnected' | 'connecting' | 'connected' | 'error';
export type AuthStatus = 'none' | 'checking' | 'authenticated' | 'unauthenticated' | 'authenticating' | 'unauthorized';

interface WalletState {
  address: string | null;
  isConnected: boolean;
  status: WalletStatus;
  error: string | null;
  provider: any | null; // Store the provider instance for advanced use cases
  // Session-related state
  isAuthenticated: boolean;
  authStatus: AuthStatus;
  sessionToken: string | null;
  sessionExpiresAt: string | null;
  authError: string | null;
  // Authorization state
  isAuthorized: boolean | null;
  authorizationMessage: string | null;
  authorizationSuggestion: string | null;
}

interface WalletContextProps extends WalletState {
  connect: () => Promise<void>;
  disconnect: () => void;
  signMessage: (message: string) => Promise<string>;
  // Session-related methods
  authenticateWallet: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  revokeSession: () => Promise<void>;
  // Authorization methods
  checkWalletAuthorization: () => Promise<void>;
  // May add more wallet actions here later e.g. switchNetwork, getBalance
}

const initialState: WalletState = {
  address: null,
  isConnected: false,
  status: 'disconnected',
  error: null,
  provider: null,
  // Session-related initial state
  isAuthenticated: false,
  authStatus: 'none',
  sessionToken: null,
  sessionExpiresAt: null,
  authError: null,
  // Authorization initial state
  isAuthorized: null,
  authorizationMessage: null,
  authorizationSuggestion: null,
};

export const WalletContext = createContext<WalletContextProps>(
  initialState as WalletContextProps // Cast to allow initial undefined methods
);

interface WalletProviderProps {
  children: ReactNode;
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const [walletState, setWalletState] = useState<WalletState>(initialState);

  const updateState = useCallback((updates: Partial<WalletState>) => {
    setWalletState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Check for persisted connection and session on mount
  useEffect(() => {
    const initializeWallet = async () => {
      const previouslyConnectedAddress = localStorage.getItem('wallet_address');
      const existingSessionToken = localStorage.getItem('wallet_session_token');
      
      if (previouslyConnectedAddress) {
        try {
          // Actually re-establish the MetaMask connection
          updateState({ status: 'connecting' });
          const connectedAddress = await walletService.connectWallet();
          
          updateState({
            address: connectedAddress,
            isConnected: true,
            status: 'connected',
            provider: walletService.getProvider(),
            error: null,
            // If we have an existing session token, set it in state temporarily
            sessionToken: existingSessionToken,
          });

          // Check authentication status after re-connecting (this will validate the token)
          await checkAuthStatusInternal(connectedAddress);
        } catch (error: any) {
          console.warn('Failed to re-establish wallet connection:', error);
          // If re-connection fails, clear the stored address and session token
          localStorage.removeItem('wallet_address');
          localStorage.removeItem('wallet_session_token');
          updateState({
            address: null,
            isConnected: false,
            status: 'disconnected',
            provider: null,
            error: null,
            authStatus: 'none',
            sessionToken: null,
          });
        }
      } else {
        // No wallet address but session token exists - clear it
        if (existingSessionToken) {
          localStorage.removeItem('wallet_session_token');
        }
      }
    };

    initializeWallet();
  }, [updateState]);

  const checkAuthStatusInternal = useCallback(async (address?: string) => {
    const walletAddress = address || walletState.address;
    if (!walletAddress) {
      // Clear localStorage when no wallet address
      localStorage.removeItem('wallet_session_token');
      updateState({
        isAuthenticated: false,
        authStatus: 'none',
        sessionToken: null,
        sessionExpiresAt: null,
        authError: null,
      });
      return;
    }

    updateState({ authStatus: 'checking', authError: null });

    try {
      const sessionStatus = await walletService.getSessionStatus();
      
      // Save/update session token to localStorage for API client access
      if (sessionStatus.is_authorized && sessionStatus.session_token) {
        localStorage.setItem('wallet_session_token', sessionStatus.session_token);
        console.log('🔐 Session token synced to localStorage (status check)');
      } else {
        localStorage.removeItem('wallet_session_token');
        console.log('🔐 Session token cleared from localStorage (status check)');
      }
      
      updateState({
        isAuthenticated: sessionStatus.is_authorized,
        authStatus: sessionStatus.is_authorized ? 'authenticated' : 'unauthenticated',
        sessionToken: sessionStatus.session_token || null,
        sessionExpiresAt: sessionStatus.expires_at || null,
        authError: null,
      });
    } catch (error: any) {
      console.error('Error checking auth status:', error);
      // Clear localStorage on error
      localStorage.removeItem('wallet_session_token');
      updateState({
        isAuthenticated: false,
        authStatus: 'unauthenticated',
        sessionToken: null,
        sessionExpiresAt: null,
        authError: error.message || 'Failed to check authentication status',
      });
    }
  }, [walletState.address, updateState]);

  const handleAccountsChanged = useCallback((accounts: string[]) => {
    if (accounts.length === 0) {
      // Clear localStorage when disconnecting
      localStorage.removeItem('wallet_session_token');
      updateState({ 
        address: null, 
        isConnected: false, 
        status: 'disconnected', 
        provider: null,
        error: 'User disconnected accounts.',
        // Reset session state
        isAuthenticated: false,
        authStatus: 'none',
        sessionToken: null,
        sessionExpiresAt: null,
        authError: null,
      });
      localStorage.removeItem('wallet_address');
    } else {
      const newAddress = accounts[0];
      // Clear previous session token when switching accounts
      localStorage.removeItem('wallet_session_token');
      updateState({ 
        address: newAddress, 
        isConnected: true, 
        status: 'connected', 
        error: null,
        // Reset session state for new address
        isAuthenticated: false,
        authStatus: 'none',
        sessionToken: null,
        sessionExpiresAt: null,
        authError: null,
      });
      localStorage.setItem('wallet_address', newAddress);
      
      // Check auth status for new address
      checkAuthStatusInternal(newAddress);
    }
  }, [updateState, checkAuthStatusInternal]);

  const handleDisconnectEvent = useCallback(() => {
    // Clear localStorage when wallet disconnects
    localStorage.removeItem('wallet_session_token');
    updateState({
      address: null,
      isConnected: false,
      status: 'disconnected',
      provider: null,
      error: 'Wallet disconnected.',
      // Reset session state
      isAuthenticated: false,
      authStatus: 'none',
      sessionToken: null,
      sessionExpiresAt: null,
      authError: null,
    });
    localStorage.removeItem('wallet_address');
  }, [updateState]);

  // Effect to subscribe to walletService provider events
  useEffect(() => {
    const currentProvider = walletService.getProvider();
    if (currentProvider && typeof currentProvider.on === 'function') {
      currentProvider.on('accountsChanged', handleAccountsChanged);
      currentProvider.on('disconnect', handleDisconnectEvent);

      return () => {
        if (currentProvider && typeof currentProvider.removeListener === 'function') {
          currentProvider.removeListener('accountsChanged', handleAccountsChanged);
          currentProvider.removeListener('disconnect', handleDisconnectEvent);
        }
      };
    }
  }, [walletState.provider, handleAccountsChanged, handleDisconnectEvent]); // Re-run if provider changes

  const connect = useCallback(async () => {
    updateState({ status: 'connecting', error: null });
    try {
      const connectedAddress = await walletService.connectWallet();
      updateState({
        address: connectedAddress,
        isConnected: true,
        status: 'connected',
        provider: walletService.getProvider(),
        error: null,
      });

      // Check authorization first, then authentication status
      try {
        const authResult = await walletService.checkWalletAuthorization(connectedAddress);
        updateState({
          isAuthorized: authResult.is_authorized,
          authorizationMessage: authResult.message,
          authorizationSuggestion: authResult.suggestion || null,
        });
      } catch (err) {
        console.error('Authorization check failed:', err);
        updateState({
          isAuthorized: false,
          authorizationMessage: 'Failed to check wallet authorization',
          authorizationSuggestion: 'Please try again or contact support',
        });
      }

      // Check authentication status after connecting
      await checkAuthStatusInternal(connectedAddress);
    } catch (err: any) {
      console.error('Connection failed:', err);
      updateState({
        isConnected: false,
        status: 'error',
        error: err.message || 'Failed to connect wallet.',
        provider: null,
      });
    }
  }, [updateState, checkAuthStatusInternal]);

  const disconnect = useCallback(() => {
    walletService.disconnect();
    // Clear localStorage when manually disconnecting
    localStorage.removeItem('wallet_session_token');
    updateState({
      address: null,
      isConnected: false,
      status: 'disconnected',
      provider: null,
      error: null,
      // Reset session state
      isAuthenticated: false,
      authStatus: 'none',
      sessionToken: null,
      sessionExpiresAt: null,
      authError: null,
    });
  }, [updateState]);

  const signMessage = useCallback(async (message: string) => {
    if (!walletState.isConnected || !walletState.address) {
      throw new Error('Wallet not connected. Cannot sign message.');
    }
    updateState({ error: null }); // Clear previous errors
    try {
      const signature = await walletService.signMessage(message);
      return signature;
    } catch (err: any) {
      console.error('Signing failed:', err);
      updateState({ status: 'error', error: err.message || 'Failed to sign message.' });
      throw err;
    }
  }, [walletState.isConnected, walletState.address, updateState]);

  const authenticateWallet = useCallback(async () => {
    if (!walletState.isConnected || !walletState.address) {
      throw new Error('Wallet not connected. Cannot authenticate.');
    }

    updateState({ authStatus: 'authenticating', authError: null });

    try {
      const sessionResponse = await walletService.authenticateWallet();
      
      // Save session token to localStorage for API client access
      if (sessionResponse.session_token) {
        localStorage.setItem('wallet_session_token', sessionResponse.session_token);
        console.log('🔐 Session token saved to localStorage for API client access');
      }
      
      updateState({
        isAuthenticated: true,
        authStatus: 'authenticated',
        sessionToken: sessionResponse.session_token,
        sessionExpiresAt: sessionResponse.expires_at,
        authError: null,
      });
    } catch (err: any) {
      console.error('Authentication failed:', err);
      updateState({
        isAuthenticated: false,
        authStatus: 'unauthenticated',
        sessionToken: null,
        sessionExpiresAt: null,
        authError: err.message || 'Failed to authenticate wallet.',
      });
      throw err;
    }
  }, [walletState.isConnected, walletState.address, updateState]);

  const checkAuthStatus = useCallback(async () => {
    await checkAuthStatusInternal();
  }, [checkAuthStatusInternal]);

  const revokeSession = useCallback(async () => {
    try {
      await walletService.revokeSession();
      // Clear localStorage when revoking session
      localStorage.removeItem('wallet_session_token');
      updateState({
        isAuthenticated: false,
        authStatus: 'unauthenticated',
        sessionToken: null,
        sessionExpiresAt: null,
        authError: null,
      });
    } catch (err: any) {
      console.error('Session revocation failed:', err);
      updateState({
        authError: err.message || 'Failed to revoke session.',
      });
      throw err;
    }
  }, [updateState]);

  const checkWalletAuthorization = useCallback(async () => {
    if (!walletState.address) {
      updateState({
        isAuthorized: null,
        authorizationMessage: null,
        authorizationSuggestion: null,
      });
      return;
    }

    try {
      const authResult = await walletService.checkWalletAuthorization(walletState.address);
      
      updateState({
        isAuthorized: authResult.is_authorized,
        authorizationMessage: authResult.message,
        authorizationSuggestion: authResult.suggestion || null,
        authStatus: authResult.is_authorized ? walletState.authStatus : 'unauthorized',
        authError: authResult.is_authorized ? null : authResult.message,
      });
    } catch (err: any) {
      console.error('Authorization check failed:', err);
      updateState({
        isAuthorized: false,
        authorizationMessage: 'Failed to check wallet authorization',
        authorizationSuggestion: 'Please try again or contact support',
        authError: err.message || 'Failed to check authorization',
      });
    }
  }, [walletState.address, walletState.authStatus, updateState]);

  return (
    <WalletContext.Provider
      value={{
        ...walletState,
        connect,
        disconnect,
        signMessage,
        authenticateWallet,
        checkAuthStatus,
        revokeSession,
        checkWalletAuthorization,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}; 